import { useState } from 'react';

import { useCurrencyPreference } from '../../../../../shared/user-preferences/hooks/useCurrencyPreference';
import { GlobalAiChartPreviewDataReporter } from '../../../../global-ai-assistant/global-ai-assistant-chart-preview-data-reporter';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { CompensationMapDataProvider } from '../../DataProvider/compensation/CompensationMapDataProvider';
import { CompositionMapDataProvider } from '../../DataProvider/composition/CompositionMapDataProvider';
import { PostingDiscoveryDataProvider } from '../../DataProvider/PostingDiscoveryDataProvider';
import { PostingMapDataProvider } from '../../DataProvider/PostingMapDataProvider';
import { ScreenerMapDataProvider } from '../../DataProvider/ScreenerMapDataProvider';
import { SentimentMapDataProvider } from '../../DataProvider/SentimentMapDataProvider';
import { TalentDiscoveryDataProvider } from '../../DataProvider/TalentDiscoveryDataProvider';
import { TalentDiscoveryMapDataProvider } from '../../DataProvider/TalentDiscoveryMapDataProvider';
import { DataProviderQueryStatus } from '../../DataProvider/types';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../PageConfigProvider/types';
import type {
  DataNormalization,
  DataTransformation,
  MapPlotOptions,
} from '../../PageConfigProvider/types/item';
import {
  isNonHeadcountCompositionMetric,
  isPayMetric,
  isPostingMetric,
  isScreenerMetric,
  isSentimentMetric,
  isTalentDiscoveryMetric,
  type MetricValues,
} from '../../PageConfigProvider/types/metric';
import { ChartFallback } from '../ChartFallback';
import { ChartLoader } from '../ChartLoader';
import { ChartNotSupported } from '../ChartNotSupported';
import { useChart } from '../ChartProvider';
import { GenericMap } from './GenericMap';
import styles from './map-chart-selector.module.css';
import type { MapPoint } from './types';

const unsupportedCompositionMapError = new Error(
  'Per-place data is not available for this metric. Choose a different chart type.'
);

const MapWithLoader = ({
  points,
  metric,
  normalization,
  transformation,
  primaryDimension,
  interactive = true,
  testId,
  'data-testid': dataTestId,
}: {
  points: MapPoint[];
  metric: MetricValues;
  normalization?: DataNormalization;
  transformation?: DataTransformation;
  primaryDimension?: PrimaryFilterId;
  interactive?: boolean;
  testId?: string;
  'data-testid'?: string;
}) => {
  const [isMapReady, setIsMapReady] = useState(false);

  const resolvedTestId = testId ?? dataTestId;

  return (
    <div className={styles.mapFill}>
      <GenericMap
        data-testid={resolvedTestId}
        points={points}
        metric={metric}
        normalization={normalization}
        transformation={transformation}
        primaryDimension={primaryDimension}
        interactive={interactive}
        isSourceReady={setIsMapReady}
      />
      {!isMapReady && <ChartLoader />}
    </div>
  );
};

/**
 * Reports the map's data to the assistant like every other chart family: the
 * chart preview settles on it (a `render_chart` waits for that), and a custom
 * page's narration reads it. Self-guarded, so it is inert on ordinary pages.
 */
const MapPreviewDataReporter = ({
  status,
  error,
  data,
  metric,
}: {
  status: DataProviderQueryStatus;
  error: Error | undefined;
  data: { points: MapPoint[] } | undefined;
  metric: MetricValues;
}) => {
  const { item } = useChart();
  return (
    <GlobalAiChartPreviewDataReporter
      itemId={item.id}
      branch="map"
      status={status}
      error={error}
      data={data}
      chartType="map"
      metric={metric}
    />
  );
};

export const MapChartSelector = ({
  primaryDimension,
  metric,
  mapOptions,
}: {
  primaryDimension?: PrimaryFilterId;
  metric: MetricValues;
  mapOptions: MapPlotOptions;
}) => {
  const { revelioPageId } = useTabContentConfig();
  const effectiveFilters = useEffectiveFilters();
  const hasVisibleFilters = effectiveFilters.some((filter) => filter.isVisible);
  const isTalentDiscovery = revelioPageId === RevelioPageId.TALENT_DISCOVERY;
  const isPostingsDiscovery = revelioPageId === RevelioPageId.POSTING_DISCOVERY;
  const isInteractive = !(
    (isTalentDiscovery || isPostingsDiscovery) &&
    !hasVisibleFilters
  );
  const { exchangeRate } = useCurrencyPreference();

  if (isTalentDiscoveryMetric(metric) && isPostingsDiscovery) {
    return (
      <PostingDiscoveryDataProvider>
        {({ data, status, error }) => (
          <>
            <MapPreviewDataReporter
              status={status}
              error={error}
              data={data?.mapData}
              metric={metric}
            />
            <ChartFallback status={status}>
              <MapWithLoader
                data-testid="posting-discovery-map"
                points={data?.mapData?.points ?? []}
                metric={metric}
                normalization={mapOptions?.normalization}
                transformation={mapOptions?.transformation}
                primaryDimension={primaryDimension}
                interactive={isInteractive}
              />
            </ChartFallback>
          </>
        )}
      </PostingDiscoveryDataProvider>
    );
  }

  if (isTalentDiscoveryMetric(metric)) {
    return (
      <TalentDiscoveryDataProvider exchangeRate={exchangeRate}>
        {({ data, status, error }) => (
          <>
            <MapPreviewDataReporter
              status={status}
              error={error}
              data={data?.mapData}
              metric={metric}
            />
            <ChartFallback status={status}>
              <MapWithLoader
                data-testid="talent-discovery-map"
                points={data?.mapData?.points ?? []}
                metric={metric}
                normalization={mapOptions?.normalization}
                transformation={mapOptions?.transformation}
                primaryDimension={primaryDimension}
                interactive={isInteractive}
              />
            </ChartFallback>
          </>
        )}
      </TalentDiscoveryDataProvider>
    );
  }
  if (metric === 'headcount') {
    return (
      <TalentDiscoveryMapDataProvider>
        {({ data, status, error }) => (
          <>
            <MapPreviewDataReporter
              status={status}
              error={error}
              data={data}
              metric={metric}
            />
            <ChartFallback status={status}>
              <MapWithLoader
                data-testid="talent-discovery-map"
                points={data?.points ?? []}
                metric={metric}
                normalization={mapOptions?.normalization}
                transformation={mapOptions?.transformation}
                primaryDimension={primaryDimension}
                interactive={isInteractive}
              />
            </ChartFallback>
          </>
        )}
      </TalentDiscoveryMapDataProvider>
    );
  }
  if (isScreenerMetric(metric)) {
    if (!primaryDimension) return <ChartNotSupported />;
    return (
      <ScreenerMapDataProvider
        primaryDimension={primaryDimension}
        metric={metric}
        chartType="map"
      >
        {({ data, status, error }) => (
          <>
            <MapPreviewDataReporter
              status={status}
              error={error}
              data={data}
              metric={metric}
            />
            <ChartFallback status={status}>
              <MapWithLoader
                data-testid="screener-map"
                points={data?.points ?? []}
                metric={metric}
                normalization={mapOptions?.normalization}
                transformation={mapOptions?.transformation}
              />
            </ChartFallback>
          </>
        )}
      </ScreenerMapDataProvider>
    );
  }

  if (isPostingMetric(metric)) {
    if (!primaryDimension) return <ChartNotSupported />;
    return (
      <PostingMapDataProvider
        primaryDimension={primaryDimension}
        metric={metric}
        exchangeRate={exchangeRate}
      >
        {({ data, status, error }) => (
          <>
            <MapPreviewDataReporter
              status={status}
              error={error}
              data={data}
              metric={metric}
            />
            <ChartFallback status={status}>
              <MapWithLoader
                data-testid="posting-map"
                points={data?.points ?? []}
                metric={metric}
                normalization={mapOptions?.normalization}
                transformation={mapOptions?.transformation}
              />
            </ChartFallback>
          </>
        )}
      </PostingMapDataProvider>
    );
  }

  if (isSentimentMetric(metric)) {
    if (!primaryDimension) return <ChartNotSupported />;
    return (
      <SentimentMapDataProvider
        primaryDimension={primaryDimension}
        metric={metric}
      >
        {({ data, status, error }) => (
          <>
            <MapPreviewDataReporter
              status={status}
              error={error}
              data={data}
              metric={metric}
            />
            <ChartFallback status={status}>
              <MapWithLoader
                data-testid="sentiment-map"
                points={data?.points ?? []}
                metric={metric}
                normalization={mapOptions?.normalization}
                transformation={mapOptions?.transformation}
              />
            </ChartFallback>
          </>
        )}
      </SentimentMapDataProvider>
    );
  }

  if (isPayMetric(metric)) {
    if (!primaryDimension) return <ChartNotSupported />;
    return (
      <CompensationMapDataProvider
        primaryDimension={primaryDimension}
        exchangeRate={exchangeRate}
      >
        {({ data, status, error }) => (
          <>
            <MapPreviewDataReporter
              status={status}
              error={error}
              data={data}
              metric={metric}
            />
            <ChartFallback status={status}>
              <MapWithLoader
                data-testid="compensation-map"
                points={data?.points ?? []}
                metric={metric}
                normalization={mapOptions?.normalization}
                transformation={mapOptions?.transformation}
              />
            </ChartFallback>
          </>
        )}
      </CompensationMapDataProvider>
    );
  }

  // composition_v3 geography rows contain headcount, not the selected rate or average.
  if (isNonHeadcountCompositionMetric(metric)) {
    return (
      <>
        <MapPreviewDataReporter
          status={DataProviderQueryStatus.Error}
          error={unsupportedCompositionMapError}
          data={undefined}
          metric={metric}
        />
        <ChartNotSupported>
          {unsupportedCompositionMapError.message}
        </ChartNotSupported>
      </>
    );
  }

  // Default to Composition
  if (!primaryDimension) return <ChartNotSupported />;
  return (
    <CompositionMapDataProvider primaryFilterId={primaryDimension}>
      {({ data, status, error }) => (
        <>
          {/* composition_v3 breaks geography down by headcount alone, so
              whatever metric is selected, these points are headcounts. */}
          <MapPreviewDataReporter
            status={status}
            error={error}
            data={data}
            metric="headcount"
          />
          <ChartFallback status={status}>
            <MapWithLoader
              data-testid="composition-map"
              points={data?.points ?? []}
              metric={metric}
              normalization={mapOptions?.normalization}
              transformation={mapOptions?.transformation}
            />
          </ChartFallback>
        </>
      )}
    </CompositionMapDataProvider>
  );
};
